<template>
  <div>
    <div v-if="loading" class="mt-n12 pb-9">
      <TableSkeleton />
    </div>

    <div
      class="d-flex justify-end align-end mt-n11 me-6 mb-2"
      v-if="!loading && totalExecutions > 0"
    >
      <ColumnsSelector
        persistenceKey="routineView_routineExecutions"
        :headers="headers"
        @toggleColumn="onToggleColumn"
        @setInitialColumns="setInitialColumns"
      />
    </div>

    <v-data-table
      v-show="!loading"
      class="row-pointer"
      data-private
      item-key="id"
      :footer-props="footerOptions"
      :headers="columns"
      :items="executions"
      :loading="loading"
      :no-data-text="$t('routines.executions_empty')"
      :options.sync="options"
      :server-items-length="totalExecutions"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @update:options="trackOptions"
    >
      <template v-slot:[`item.name`]="{ item }">
        <router-link
          class="cursor-pointer text-decoration-none link-td"
          :to="`/routine-executions/${item.id}`"
        >
          <span
            v-snip="1"
            class="body-2 link-td"
            :title="item.name"
            @click="handleExecutionLinkClick(item.id)"
            >{{ item.name }}</span
          >
        </router-link>
      </template>
      <template v-slot:[`item.site`]="{ item }">
        <p class="mb-0">
          {{ getSiteName(item.site) }}
        </p>
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        <p class="mb-0">
          {{ formatDate(item.createdAt, 'MMM D YYYY, hh:mm a') }}
        </p>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <Status :entity="item" />
      </template>
      <template v-slot:[`item.scorePercentage`]="{ item }">
        <p
          class="mb-0 text-center"
          data-testid="routineExecutions-text-scorePercentage"
        >
          {{ formattedExecutionScorePercentage(item) }}
        </p>
      </template>
      <template v-slot:[`item.checklist`]="{ item }">
        <p class="mb-0 text-center">
          {{ item.checklistResponse ? $t('common.yes') : $t('common.no') }}
        </p>
      </template>
      <template v-slot:[`item.createdBy`]="{ item }">
        <ChipAvatar small :account="item.createdBy" />
      </template>
      <template v-slot:[`item.totalApprovals`]="{ item }">
        <div class="d-flex justify-center">
          <p class="mb-0">
            {{ item.totalApprovals || $t('common.none') }}
          </p>
        </div>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex justify-center">
          <Button
            v-if="shouldShowApprovalAction(item)"
            color="blueGrey"
            data-testid="routineExecutions-button-approve"
            icon="mdi-checkbox-marked-circle-plus-outline"
            :tooltip="$t('routines.approval.approve')"
            @click.stop="handleApprovalClick(item)"
          />

          <Button
            color="blueGrey"
            data-testid="routineExecutions-button-delete"
            icon="mdi-trash-can-outline"
            :disabled="
              !getRoutinePermissions({ routine: item, routineDefinition })
                .delete
            "
            :tooltip="
              getRoutinePermissions({ routine: item, routineDefinition }).delete
                ? $t('common.button.delete')
                : $t('common.no_permissions')
            "
            @click.stop="handleDeleteClick(item)"
          />
        </div>
      </template>
    </v-data-table>

    <DeleteConfirmationModal
      :show="showDeleteDialog"
      :isLoading="isDeleting"
      :title="$t('routines.delete_excution_title')"
      :text="deleteText"
      @cancel="showDeleteDialog = false"
      @delete="removeRoutine"
    />

    <RoutineApprovalDialog
      v-if="showApprovalDialog"
      shouldFetchCurrentAccountApproval
      :routine="selectedRoutine"
      :routineName="selectedRoutine.name"
      :show="showApprovalDialog"
      :routineId="selectedRoutine.id"
      @approved="handleApprovalChange"
      @close="handleCloseDialog"
      @removed="handleApprovalChange"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import api from '@/api'

import mixin from '@/mixins/mixin'
import routinePermissions from '@/mixins/routinePermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import userMixin from '@/mixins/userMixin.vue'

import Status from '@/components/Core/StatusChip.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import Button from '@/components/UI/Button'
import TableSkeleton from '@/components/TableSkeleton.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import RoutineApprovalDialog from '@/components/Routines/RoutineApproval/RoutineApprovalDialog.vue'

const DEFAULT_SIZE = 20
const DEFAULT_SORT = 'createdAt'

const sortOrder = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

const DEFAULT_COLUMNS = [
  'name',
  'createdAt',
  'status',
  'scorePercentage',
  'checklist',
  'createdBy',
  'totalApprovals',
  'actions',
]

export default {
  name: 'RoutineExecutions',

  mixins: [mixin, userMixin, trackerMixin, routinePermissions],

  props: {
    executions: Array,
    loading: Boolean,
    totalExecutions: { type: Number, String },
    routineDefinition: {
      type: Object,
      required: true,
    },
  },

  components: {
    Status,
    ChipAvatar,
    DeleteConfirmationModal,
    Button,
    TableSkeleton,
    ColumnsSelector,
    RoutineApprovalDialog,
  },

  data() {
    return {
      firstLoading: true,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      currentPage: 1,
      isDeleting: false,
      showDeleteDialog: false,
      routineToDelete: {},
      sortBy: DEFAULT_SORT,
      sortDesc: true,
      selectedRoutine: null,
      showApprovalDialog: false,
      headers: [
        {
          text: this.$t('routines.execution_header_name'),
          align: 'start',
          sortable: true,
          value: 'name',
          width: 243,
          show: true,
        },
        {
          text: this.$t('common.site_one'),
          align: 'start',
          sortable: true,
          value: 'site',
          width: 200,
          show: true,
        },
        {
          text: this.$t('common.date.date'),
          align: 'start',
          sortable: true,
          value: 'createdAt',
          width: 216,
          show: true,
        },
        {
          text: this.$t('common.status'),
          align: 'start',
          sortable: true,
          value: 'status',
          width: 130,
          show: true,
        },
        {
          text: this.$t('routines.execution.score_percentage_header'),
          align: 'center',
          sortable: true,
          value: 'scorePercentage',
          width: 110,
          show: true,
        },
        {
          text: this.$t('routines.checklist_answered'),
          align: 'center',
          sortable: false,
          value: 'checklist',
          width: 110,
          show: true,
        },
        {
          text: this.$t('routines.executed_by'),
          align: 'start',
          sortable: true,
          value: 'createdBy',
          width: 180,
          show: true,
        },
        {
          text: this.$t('routines.approval.approvals_tab_title'),
          align: 'center',
          sortable: false,
          value: 'totalApprovals',
          show: true,
        },
        {
          text: this.$t('common.action_other'),
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 82,
          show: true,
        },
      ],
    }
  },

  created() {
    this.setInitialColumns()
  },

  computed: {
    ...mapGetters(['organizationId', 'organizationSites']),

    options: {
      get() {
        return {
          itemsPerPage: DEFAULT_SIZE,
          multiSort: false,
          mustSort: true,
          page: this.currentPage,
        }
      },
      set(event) {
        this.$emit('getRoutineExecutions', {
          itemsPerPage: event.itemsPerPage,
          page: event.page,
          sortBy: event.sortBy[0] ?? DEFAULT_SORT,
          sortDesc: event.sortDesc[0] ?? true,
        })
      },
    },

    deleteText() {
      return this.routineToDelete?.findings?.length
        ? this.$t('routines.delete_execution_with_findings')
        : this.$t('routines.delete_execution_confirmation')
    },

    columns() {
      return this.headers.filter(h => h.show)
    },
  },

  methods: {
    formatDate(date, format) {
      return utils.dates.formatDate(date, format)
    },

    formattedExecutionScorePercentage(execution) {
      if (execution?.status !== 'completed') {
        return this.$t('common.not_applicable')
      }

      const rawValue = execution?.scorePercentage
      const scoreIsMissing =
        rawValue === null || rawValue === undefined || rawValue === ''

      if (scoreIsMissing) {
        return this.$t('common.not_applicable')
      }

      const hasValidScore = Number.isFinite(Number(rawValue))

      if (!hasValidScore) {
        return this.$t('common.not_applicable')
      }

      const roundedValue = Number(Number(rawValue).toFixed(1))

      return this.$t('routines.execution.percentage_value', {
        value: roundedValue,
      })
    },

    getSiteName(siteId) {
      return this.organizationSites.find(site => siteId === site.id)?.name ?? ''
    },

    handleDeleteClick(routine) {
      this.routineToDelete = routine
      this.trackRoutineDetailsTable({
        action: 'deleteRoutineExecution',
        id: routine.id,
      })
      this.showDeleteDialog = true
    },

    async removeRoutine() {
      try {
        this.isDeleting = true
        await api.routine.deleteRoutineExecutions(
          this.organizationId,
          this.routineToDelete.id
        )

        this.$emit('getRoutineExecutions', {
          ...this.options,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        })
        this.trackRoutineExecutionDelete({
          id: this.routineToDelete.id,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
        this.showDeleteDialog = false
      }
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
      const name = this.headers[index].text

      this.trackRoutineDetailsTable({
        action: 'changeColumns',
        source: 'routineDetailsTable.executions',
        column: { [name]: value },
        routineDefinition: this.routineDefinition.id,
      })
    },

    trackOptions(options) {
      if (!this.firstLoading) {
        this.trackRoutineDetailsTable({
          action: 'getExecutions',
          sortBy: options.sortBy,
          page: options.page,
          order: options?.sortDesc ? sortOrder.DESCENDING : sortOrder.ASCENDING,
        })
      }
      this.firstLoading = false
    },

    handleExecutionLinkClick(id) {
      this.trackRoutineDetailsTable({
        action: 'goToExecution',
        id: id,
        source: 'executionsTable',
      })
    },

    setInitialColumns(selectedColumns = DEFAULT_COLUMNS) {
      this.headers = this.getInitialSelectedTableColumns(
        this.headers,
        selectedColumns
      )
    },

    shouldShowApprovalAction(routine) {
      const permissions = this.getRoutinePermissions({
        routine,
        routineDefinition: this.routineDefinition,
      })

      return Boolean(permissions.approve)
    },

    async handleApprovalClick(routine) {
      if (routine.status !== 'completed') {
        this.showInfo(
          this.$t('routines.approval.routine_not_completed_message')
        )
        return
      }

      if (!this.shouldShowApprovalAction(routine)) {
        return
      }

      this.selectedRoutine = routine
      this.showApprovalDialog = true
    },

    handleApprovalChange() {
      this.$emit('getRoutineExecutions', {
        ...this.options,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
      })
      this.handleCloseDialog()
    },

    handleCloseDialog() {
      this.selectedRoutine = null
      this.showApprovalDialog = false
    },
  },
}
</script>
